import { AxiosResponse } from 'axios';
import moment from 'moment';
import { getDateTimeFormat, getRawDateTimeFormatInfo } from '../../helpers/DateTimeUtility';
import { getFeature } from '../../helpers/PermissionUtility';
import { Features } from '../../helpers/PermissionUtility-types';
import { EditorTypes } from '../../helpers/constants';
import {
  AutoTag,
  BasicElement,
  DefaultFieldDisplaySettings,
  ItemCollection,
  ItemCollectionItem,
  NamedFunctionType,
  PrintConfig,
  Template,
  TemplateAction,
  TemplateCoordinates,
  TemplateField,
  TemplatePage
} from '../../types/template-types';
import {
  BasicElementAction,
  BasicElementThumbnail,
  BasicElementUpload,
  KeyPath,
  TemplateEditorAction,
  TemplateEditorFormula,
  TemplateEditorPayload,
  TemplateEditorValue,
  TemplateInsertDirection,
  TemplateSelectionRectangle,
  TemplateViewIndex,
  TemplateViewType
} from '../templateeditor-types';
import {
  TEMPLATE_ADD_BASIC_ELEMENT,
  TEMPLATE_ADD_BASIC_ELEMENTS_ACTION,
  TEMPLATE_ADD_BASIC_ELEMENT_UPLOAD,
  TEMPLATE_ADD_COLUMN,
  TEMPLATE_ADD_DATALAYOUTPOSITION,
  TEMPLATE_ADD_DATALAYOUTSECTION,
  TEMPLATE_ADD_DATALAYOUTTAB,
  TEMPLATE_ADD_FIELDPOSITION,
  TEMPLATE_ADD_FIELD_BEGIN_DRAG,
  TEMPLATE_ADD_FIELD_END_DRAG,
  TEMPLATE_ADD_VIEW,
  TEMPLATE_AUTOBUILD_LISTVIEW,
  TEMPLATE_CLEAR_SELECTION,
  TEMPLATE_CLIPBOARD_SET_VISIBLE,
  TEMPLATE_CLOSE_ERROR,
  TEMPLATE_CLOSE_ERROR_MESSAGE,
  TEMPLATE_CURRENT_VIEW_TAB_INDEX,
  TEMPLATE_DELETE_COLUMN,
  TEMPLATE_DELETE_DATALAYOUTTAB,
  TEMPLATE_DELETE_SELECTED_ITEMS,
  TEMPLATE_DESELECT_FIELDPOSITIONS,
  TEMPLATE_DUPLICATE_DATALAYOUTTAB,
  TEMPLATE_EDITOR_ADD_DRAFT_PAGE_THUMBNAIL,
  TEMPLATE_EDTIOR_SHOW_FIELDS_WITH_ELEMENTS,
  TEMPLATE_FORMULA_BLUR,
  TEMPLATE_FORMULA_CHANGE,
  TEMPLATE_FORMULA_FOCUS,
  TEMPLATE_GET_FAILURE,
  TEMPLATE_GET_REQUEST,
  TEMPLATE_GET_SUCCESS,
  TEMPLATE_MOVE_DATALAYOUTPOSITION,
  TEMPLATE_MOVE_DATALAYOUTSECTION,
  TEMPLATE_DELETE_DATALAYOUTSECTION,
  TEMPLATE_MOVE_DATALAYOUTTAB,
  TEMPLATE_PUBLISH_REQUEST,
  TEMPLATE_PUBLISH_SUCCESS,
  TEMPLATE_REMOVE_BASIC_ELEMENTS,
  TEMPLATE_REMOVE_VIEW,
  TEMPLATE_SAVE_REQUEST,
  TEMPLATE_SAVE_SUCCESS,
  TEMPLATE_SELECT_ALL_FIELDPOSITIONS_ON_PAGE,
  TEMPLATE_SELECT_BASIC_ELEMENTS,
  TEMPLATE_SELECT_COLUMNPOSITIONS,
  TEMPLATE_SELECT_COLUMNS,
  TEMPLATE_SELECT_DATALAYOUTPOSITION,
  TEMPLATE_SELECT_DATALAYOUTSECTION,
  TEMPLATE_SELECT_DATALAYOUTTAB,
  TEMPLATE_SELECT_FIELDPOSITIONS,
  TEMPLATE_SELECT_FIELDPOSITIONS_IN_RECTANGLE,
  TEMPLATE_SELECT_FIELDS,
  TEMPLATE_SELECT_PAGES,
  TEMPLATE_SELECT_ROWPOSITIONS,
  TEMPLATE_SET_CURRENT_PAGE,
  TEMPLATE_SET_CURRENT_TAB,
  TEMPLATE_SWITCH_ACTIVE_EDITOR,
  TEMPLATE_UNMAP_SELECTED_ITEMS,
  TEMPLATE_UPDATE_AUTOTAGS,
  TEMPLATE_UPDATE_BASIC_ELEMENTS_GROUP_ALIGN,
  TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES,
  TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES_GROUPED_HISTORY,
  TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES_SYSTEM_UPDATE,
  TEMPLATE_UPDATE_BASIC_ELEMENTS_Z_INDEX,
  TEMPLATE_UPDATE_COLUMNPOSITION_DISPLAYTYPE,
  TEMPLATE_UPDATE_COLUMNPOSITION_HIDDEN,
  TEMPLATE_UPDATE_COLUMNPOSITION_PROPERTY,
  TEMPLATE_UPDATE_COLUMNPOSITION_WIDTH,
  TEMPLATE_UPDATE_COLUMNPOSITION_WIDTH_BY_OFFSET,
  TEMPLATE_UPDATE_COLUMN_FIELDTYPE,
  TEMPLATE_UPDATE_COLUMN_MOVE,
  TEMPLATE_UPDATE_COLUMN_PROPERTY,
  TEMPLATE_UPDATE_COLUMN_PROPERTY_NAME,
  TEMPLATE_UPDATE_CUSTOMACTIONS,
  TEMPLATE_UPDATE_DATALAYOUTPOSITION_PROPERTY,
  TEMPLATE_UPDATE_DATALAYOUTSECTION_PROPERTY,
  TEMPLATE_UPDATE_DATALAYOUTTAB_PROPERTY,
  TEMPLATE_UPDATE_DEFAULTFIELDDISPLAYSETTINGS,
  TEMPLATE_UPDATE_DEFAULTFORMNAME,
  TEMPLATE_UPDATE_FIELDPOSITION_COORDINATES,
  TEMPLATE_UPDATE_FIELDPOSITION_COORDINATES_BY_OFFSET,
  TEMPLATE_UPDATE_FIELDPOSITION_DISPLAYTYPE,
  TEMPLATE_UPDATE_FIELDPOSITION_FONTSIZE,
  TEMPLATE_UPDATE_FIELDPOSITION_LINEHEIGHT,
  TEMPLATE_UPDATE_FIELDPOSITION_NUMBEROFROWS,
  TEMPLATE_UPDATE_FIELDPOSITION_PROPERTY,
  TEMPLATE_UPDATE_FIELDPOSITION_STARTINGROW,
  TEMPLATE_UPDATE_FIELD_DATASOURCE,
  TEMPLATE_UPDATE_FIELD_DROPDOWNITEMS,
  TEMPLATE_UPDATE_FIELD_FORMULA,
  TEMPLATE_UPDATE_FIELD_NAME,
  TEMPLATE_UPDATE_FIELD_PROPERTY,
  TEMPLATE_UPDATE_FIELD_ROWCOUNT,
  TEMPLATE_UPDATE_FIELD_TYPE,
  TEMPLATE_UPDATE_IDENTIFIERFIELDID,
  TEMPLATE_UPDATE_NAME,
  TEMPLATE_UPDATE_NAMEDFUNCTIONS,
  TEMPLATE_UPDATE_PAGES,
  TEMPLATE_UPDATE_PAGE_PROPERTY,
  TEMPLATE_UPDATE_PRINTCONFIGS,
  TEMPLATE_UPDATE_PROPERTY,
  TEMPLATE_UPDATE_ROWPOSITION_HEIGHT,
  TEMPLATE_UPDATE_ROWPOSITION_HEIGHT_BY_OFFSET,
  TEMPLATE_UPDATE_ROWPOSITION_PROPERTY,
  TEMPLATE_UPDATE_SELECTED_FIELDPOSITION_COORDINATES_BY_OFFSET,
  TEMPLATE_UPDATE_SELECTED_ITEMS_COORDINATES_BY_OFFSET,
  TEMPLATE_UPDATE_THUMBNAILFILEID,
  TEMPLATE_UPDATE_NAME_INPUT_ACCESSED,
  TEMPLATE_NAV_TO_FIELD
} from './actionTypes';

export const formulaFocus = (
  property: string,
  value: TemplateEditorValue,
  tableValue: unknown,
  row: unknown,
  rowCount: number,
  mode: string,
  maxLength: number
): TemplateEditorAction => ({
  type: TEMPLATE_FORMULA_FOCUS,
  undoable: false,
  payload: { property, value, tableValue, row, rowCount, mode, maxLength }
});

export const formulaBlur = (): TemplateEditorAction => ({
  type: TEMPLATE_FORMULA_BLUR,
  undoable: false
});

export const formulaChange = (value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_FORMULA_CHANGE,
  undoable: false,
  payload: { value }
});

export const setCurrentPage = (currentPageId: string): TemplateEditorAction => ({
  type: TEMPLATE_SET_CURRENT_PAGE,
  undoable: false,
  payload: { currentPageId }
});

export const setCurrentTabKeyPath = (tabKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_SET_CURRENT_TAB,
  undoable: false,
  payload: { tabKeyPath }
});

export const currentViewTabIndex = (currentTemplateView: TemplateViewIndex): TemplateEditorAction => ({
  type: TEMPLATE_CURRENT_VIEW_TAB_INDEX,
  undoable: false,
  payload: { currentTemplateView }
});

export const setClipboardIsVisible = (isVisible: boolean): TemplateEditorAction => ({
  type: TEMPLATE_CLIPBOARD_SET_VISIBLE,
  undoable: false,
  payload: { isVisible }
});

export const addFormPosition = (
  pageKeyPath: KeyPath,
  field: TemplateField,
  type: number,
  x: number,
  y: number,
  width: number,
  height: number,
  checkBoxGroupItemId: string
): TemplateEditorAction => ({
  type: TEMPLATE_ADD_FIELDPOSITION,
  undoable: true,
  payload: {
    pageKeyPath,
    field,
    type,
    x,
    y,
    width,
    height,
    checkBoxGroupItemId,
    rawDateFormat: getRawDateTimeFormatInfo(),
    features: {
      maxFileSize: getFeature(Features.FileAttachmentMaxFileSize),
      maxFileCount: getFeature(Features.FileAttachmentMaxFileCount),
      audioMaxLength: getFeature(Features.AudioFieldMaxLength) || 10
    }
  }
});

export const addBasicElement = (pageKeyPath: KeyPath, element: BasicElement): TemplateEditorAction => ({
  type: TEMPLATE_ADD_BASIC_ELEMENT,
  undoable: true,
  payload: {
    pageKeyPath,
    element
  }
});

export const removeBasicElements = (keyPaths: KeyPath[]): TemplateEditorAction => ({
  type: TEMPLATE_REMOVE_BASIC_ELEMENTS,
  undoable: true,
  payload: {
    keyPaths
  }
});

export const addDataLayoutPosition = (
  sectionKeyPath: KeyPath,
  field: TemplateField,
  type: number,
  insertNextToId: string,
  insertDirection: TemplateInsertDirection
): TemplateEditorAction => ({
  type: TEMPLATE_ADD_DATALAYOUTPOSITION,
  undoable: true,
  payload: {
    sectionKeyPath,
    field,
    type,
    insertNextToId,
    insertDirection,
    rawDateFormat: getRawDateTimeFormatInfo(),
    features: {
      maxFileSize: getFeature(Features.FileAttachmentMaxFileSize),
      maxFileCount: getFeature(Features.FileAttachmentMaxFileCount),
      audioMaxLength: getFeature(Features.AudioFieldMaxLength) || 10
    }
  }
});

export const deleteDataLayoutSection = (tabKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_DELETE_DATALAYOUTSECTION,
  undoable: true,
  payload: {
    tabKeyPath
  }
});

export const moveDataLayoutPosition = (
  newSectionKeyPath: KeyPath,
  selectedKeyPaths: KeyPath[],
  insertNextToId: string,
  insertDirection: TemplateInsertDirection
): TemplateEditorAction => ({
  type: TEMPLATE_MOVE_DATALAYOUTPOSITION,
  undoable: true,
  payload: {
    newSectionKeyPath,
    selectedKeyPaths,
    insertNextToId,
    insertDirection
  }
});

export const addDataLayoutSection = (tabKeyPath: KeyPath, insertNextToId: string, insertDirection: TemplateInsertDirection): TemplateEditorAction => ({
  type: TEMPLATE_ADD_DATALAYOUTSECTION,
  undoable: true,
  payload: {
    tabKeyPath,
    insertNextToId,
    insertDirection
  }
});

export const moveDataLayoutSection = (
  previousTabKeyPath: KeyPath,
  newTabKeyPath: KeyPath,
  id: string,
  insertNextToId: string,
  insertDirection: TemplateInsertDirection
): TemplateEditorAction => ({
  type: TEMPLATE_MOVE_DATALAYOUTSECTION,
  undoable: true,
  payload: {
    previousTabKeyPath,
    newTabKeyPath,
    id,
    insertNextToId,
    insertDirection
  }
});

export const moveDataLayoutTab = (id: string, insertNextToId: string, insertDirection: TemplateInsertDirection): TemplateEditorAction => ({
  type: TEMPLATE_MOVE_DATALAYOUTTAB,
  undoable: true,
  payload: {
    id,
    insertNextToId,
    insertDirection
  }
});

export const addDataLayoutTab = (layoutKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_ADD_DATALAYOUTTAB,
  undoable: true,
  payload: { layoutKeyPath }
});

export const deleteDataLayoutTab = (tabKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_DELETE_DATALAYOUTTAB,
  undoable: true,
  payload: { tabKeyPath }
});

export const duplicateDataLayoutTab = (tabKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_DUPLICATE_DATALAYOUTTAB,
  undoable: true,
  payload: { tabKeyPath }
});

export const unmapSelectedItems = () => ({
  type: TEMPLATE_UNMAP_SELECTED_ITEMS,
  undoable: true
});

export const deleteSelectedItems = () => ({
  type: TEMPLATE_DELETE_SELECTED_ITEMS,
  undoable: true
});

export const templateEditorSelectPages = (pageKeyPaths: KeyPath[], append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_PAGES,
  payload: { pageKeyPaths, append }
});

export const templateEditorSelectFieldPositions = (fieldPositionKeyPaths: KeyPath[], append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_FIELDPOSITIONS,
  payload: { fieldPositionKeyPaths, append }
});

export const templateEditorDeselectFieldPositions = (fieldPositionKeyPaths: KeyPath[]): TemplateEditorAction => ({
  type: TEMPLATE_DESELECT_FIELDPOSITIONS,
  payload: { fieldPositionKeyPaths }
});

export const templateEditorSelectFields = (fieldIds: string[], append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_FIELDS,
  payload: { fieldIds, append }
});

export const templateEditorNavigateBackToField = (fieldIds: string[], append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_NAV_TO_FIELD,
  payload: { fieldIds, append }
});

export const templateEditorSelectColumnPositions = (
  fieldPositionKeyPath: KeyPath,
  columnIds: string[],
  append: boolean,
  rangeSelect: boolean
): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_COLUMNPOSITIONS,
  payload: { fieldPositionKeyPath, columnIds, append, rangeSelect }
});

export const templateEditorSelectRowPositions = (
  fieldPositionKeyPath: KeyPath,
  rowNumbers: number[],
  append: boolean,
  rangeSelect: boolean
): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_ROWPOSITIONS,
  payload: { fieldPositionKeyPath, rowNumbers, append, rangeSelect }
});

export const templateEditorSelectColumns = (fieldId: string, columnIds: string[], append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_COLUMNS,
  payload: { fieldId, columnIds, append }
});

export const templateEditorSelectFieldPositionsInRect = (pageId: string, rect: TemplateSelectionRectangle, append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_FIELDPOSITIONS_IN_RECTANGLE,
  payload: { pageId, rect, append }
});

export const templateEditorSelectBasicElements = (pageId: string, elements: BasicElement[]): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_BASIC_ELEMENTS,
  payload: { pageId, elements }
});

export const templateEditorClearSelection = (): TemplateEditorAction => ({
  type: TEMPLATE_CLEAR_SELECTION,
  payload: {}
});

export const templateEditorSelectAllFieldPositionsOnPage = (pageId: string): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_ALL_FIELDPOSITIONS_ON_PAGE,
  payload: { pageId }
});

export const templateEditorSelectDataLayoutPosition = (positionKeyPath: KeyPath, append: boolean, selectRange: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_DATALAYOUTPOSITION,
  payload: { positionKeyPath, append, selectRange }
});

export const templateEditorSelectDataLayoutSection = (sectionKeyPath: KeyPath, append: boolean): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_DATALAYOUTSECTION,
  payload: { sectionKeyPath, append }
});

export const templateEditorSelectDataLayoutTab = (tabKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_SELECT_DATALAYOUTTAB,
  payload: { tabKeyPath }
});

export const templateEditorUpdateName = (name: string): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_NAME,
  undoable: true,
  payload: { name }
});

export const templateEditorUpdateDefaultFormName = (defaultFormName: string): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_DEFAULTFORMNAME,
  undoable: true,
  payload: { keyPaths: [['template', 'rawDefaultFormName']], value: defaultFormName }
});

export const templateEditorUpdateAutoTags = (autoTags: AutoTag): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_AUTOTAGS,
  undoable: true,
  payload: { keyPaths: [['template', 'autoTags']], value: autoTags }
});

export const templateEditorUpdateNamedFunctions = (namedFunctions: NamedFunctionType[]): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_NAMEDFUNCTIONS,
  undoable: true,
  payload: { keyPaths: [['template', 'namedFunctions']], value: namedFunctions }
});

export const templateEditorUpdateProperty = (property: string, value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_PROPERTY,
  undoable: true,
  payload: { keyPaths: [['template', property]], value }
});

export const templateEditorUpdatePrintConfigs = (printConfigs: PrintConfig): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_PRINTCONFIGS,
  undoable: true,
  payload: { keyPaths: [['template', 'printConfigs']], value: printConfigs }
});

export const templateEditorUpdateDefaultFieldDisplaySettings = (defaultFieldDisplaySettings: DefaultFieldDisplaySettings): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_DEFAULTFIELDDISPLAYSETTINGS,
  undoable: true,
  payload: { keyPaths: [['template', 'defaultFieldDisplaySettings']], value: defaultFieldDisplaySettings }
});

export const templateEditorUpdateCustomActions = (customActions: TemplateAction[]): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_CUSTOMACTIONS,
  undoable: true,
  payload: { keyPaths: [['template', 'actions']], value: customActions }
});

export const templateEditorUpdatePages = (pages: TemplatePage[]): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_PAGES,
  undoable: true,
  payload: { keyPaths: [['template', 'pages']], value: pages }
});

export const templateEditorUpdateThumbnailFileId = (fileId: string): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_THUMBNAILFILEID,
  undoable: true,
  payload: { keyPaths: [['template', 'fileId']], value: fileId }
});

export const templateEditorAutoBuildListView = (): TemplateEditorAction => ({
  type: TEMPLATE_AUTOBUILD_LISTVIEW,
  undoable: true
});

export const templateEditorUpdateIdentifierFieldId = (fieldKeyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_IDENTIFIERFIELDID,
  undoable: true,
  payload: { fieldKeyPath }
});

export const templateEditorUpdateFieldProperty = (keyPaths: KeyPath[], value: TemplateEditorValue, formulaKey: string): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_PROPERTY,
  undoable: true,
  payload: { keyPaths, value, formulaKey }
});

export const templateEditorUpdateFieldName = (keyPath: KeyPath, value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_NAME,
  undoable: true,
  payload: { keyPath, value }
});

export const templateEditorUpdateFieldNameInputVisited = (keyPath: KeyPath): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_NAME_INPUT_ACCESSED,
  undoable: false,
  payload: { keyPath }
});

export const templateEditorUpdateFieldFormula = (keyPaths: KeyPath[], formulaKey: string, formula: TemplateEditorFormula): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_FORMULA,
  undoable: true,
  payload: { keyPaths, formulaKey, formula }
});

export const templateEditorUpdateFieldRowCount = (keyPaths: KeyPath[], rowCount: number): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_ROWCOUNT,
  undoable: true,
  payload: { keyPaths, rowCount }
});

export const templateEditorUpdateFieldDataSource = (payload: TemplateEditorPayload): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_DATASOURCE,
  undoable: true,
  payload
});

export const templateEditorUpdateFieldType = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_TYPE,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldDropDownItems = (
  keyPaths: KeyPath[],
  items: ItemCollectionItem[],
  itemCollectionTemplateId: string,
  itemCollection: ItemCollection
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELD_DROPDOWNITEMS,
  undoable: true,
  payload: { keyPaths, items, itemCollectionTemplateId, itemCollection }
});

export const templateEditorUpdatePageProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_PAGE_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldPositionProperty = (keyPaths: KeyPath[], value: TemplateEditorValue, formulaKey: string): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_PROPERTY,
  undoable: true,
  payload: { keyPaths, value, formulaKey }
});

export const templateEditorUpdateFieldPositionLineHeight = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_LINEHEIGHT,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldPositionFontSize = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_FONTSIZE,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldPositionDisplayType = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_DISPLAYTYPE,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldPositionStartingRow = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_STARTINGROW,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateFieldPositionNumberOfRows = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_NUMBEROFROWS,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorAddBasicElementsAction = (keyPaths: KeyPath[], elementAction: BasicElementAction, undoable = true): TemplateEditorAction => ({
  type: TEMPLATE_ADD_BASIC_ELEMENTS_ACTION,
  undoable: undoable,
  payload: { keyPaths, elementAction }
});

export const templateEditorUpdateBasicElementsProperties = (
  keyPaths: KeyPath[],
  update: BasicElement | BasicElement[],
  undoable = true,
  forceUpdate = false,
  systemUpdate = false
): TemplateEditorAction => ({
  type: systemUpdate ? TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES_SYSTEM_UPDATE : TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES,
  undoable: undoable,
  payload: { keyPaths, element: Array.isArray(update) ? null : update, elements: Array.isArray(update) ? update : null, forceUpdate }
});

export const templateEditorUpdateBasicElementsFile = (
  keyPaths: KeyPath[],
  update: BasicElement | BasicElement[],
  fileSrc: string,
  undoable = true
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES,
  undoable: undoable,
  payload: { keyPaths, element: Array.isArray(update) ? null : update, elements: Array.isArray(update) ? update : null, fileSrc: fileSrc, forceUpdate: false }
});

export const templateEditorUpdateBasicElementsPropertiesGroupedHistory = (
  keyPaths: KeyPath[],
  update: BasicElement | BasicElement[],
  eventId: string
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_BASIC_ELEMENTS_PROPERTIES_GROUPED_HISTORY,
  undoable: true,
  payload: { keyPaths, element: Array.isArray(update) ? null : update, elements: Array.isArray(update) ? update : null, eventId }
});

export const templateEditorUpdateBasicElementsGroupAlign = (keyPaths: KeyPath[], elementAction: BasicElementAction, undoable = true): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_BASIC_ELEMENTS_GROUP_ALIGN,
  undoable: undoable,
  payload: { keyPaths, elementAction }
});

export const templateEditorUpdateBasicElementsZIndex = (pageKeyPath: KeyPath, ids: string[]): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_BASIC_ELEMENTS_Z_INDEX,
  undoable: true,
  payload: {
    pageKeyPath,
    ids
  }
});

export const templateEditorAddBasicElementUpload = (elementUpload: BasicElementUpload): TemplateEditorAction => ({
  type: TEMPLATE_ADD_BASIC_ELEMENT_UPLOAD,
  undoable: false,
  payload: { elementUpload }
});

export const templateEditorAddDraftPageThumbnail = (pageThumbnail: BasicElementThumbnail): TemplateEditorAction => ({
  type: TEMPLATE_EDITOR_ADD_DRAFT_PAGE_THUMBNAIL,
  undoable: false,
  payload: { pageThumbnail }
});

export const templateEditorUpdateSelectedFieldPositionCoordinatesByOffset = (offset: TemplateCoordinates): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_SELECTED_FIELDPOSITION_COORDINATES_BY_OFFSET,
  undoable: true,
  payload: { offset }
});

export const templateEditorUpdateSelectedItemsCoordinatesByOffset = (offset: TemplateCoordinates): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_SELECTED_ITEMS_COORDINATES_BY_OFFSET,
  undoable: true,
  payload: { offset }
});

export const templateEditorUpdateFieldPositionCoordinates = (fieldPositionKeyPaths: KeyPath[], coordinates: TemplateCoordinates): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_COORDINATES,
  undoable: true,
  payload: { fieldPositionKeyPaths, coordinates }
});

export const templateEditorUpdateFieldPositionCoordinatesByOffset = (
  fieldPositionKeyPaths: KeyPath[],
  offset: TemplateCoordinates,
  resizeProportially: boolean
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_FIELDPOSITION_COORDINATES_BY_OFFSET,
  undoable: true,
  payload: { fieldPositionKeyPaths, offset, resizeProportially }
});

export const templateEditorUpdateColumnPositionHidden = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMNPOSITION_HIDDEN,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnPositionWidth = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMNPOSITION_WIDTH,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnPositionWidthByOffset = (
  fieldPositionKeyPath: KeyPath,
  columnId: string,
  offsetWidth: number
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMNPOSITION_WIDTH_BY_OFFSET,
  undoable: true,
  payload: { fieldPositionKeyPath, columnId, offsetWidth }
});

export const templateEditorUpdateRowPositionHeight = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_ROWPOSITION_HEIGHT,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateRowPositionHeightByOffset = (
  fieldPositionKeyPath: KeyPath,
  rowNumber: number,
  offsetHeight: number
): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_ROWPOSITION_HEIGHT_BY_OFFSET,
  undoable: true,
  payload: { fieldPositionKeyPath, rowNumber, offsetHeight }
});

export const templateEditorUpdateRowPositionProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_ROWPOSITION_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnPositionProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMNPOSITION_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnDisplayType = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMNPOSITION_DISPLAYTYPE,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMN_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateColumnPropertyName = (keyPath: KeyPath, value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMN_PROPERTY_NAME,
  undoable: true,
  payload: { keyPath, value }
});

export const templateEditorUpdateColumnFieldType = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMN_FIELDTYPE,
  undoable: true,
  payload: { keyPaths, value, rawDateFormat: getRawDateTimeFormatInfo() }
});

export const templateEditorUpdateColumnMove = (fieldId: string, columnId: string, direction: TemplateInsertDirection): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_COLUMN_MOVE,
  undoable: true,
  payload: { fieldId, columnId, direction }
});

export const templateEditorAddColumn = (fieldId: string): TemplateEditorAction => ({
  type: TEMPLATE_ADD_COLUMN,
  undoable: true,
  payload: { fieldId }
});

export const templateEditorDeleteColumn = (fieldId: string, columnId: string): TemplateEditorAction => ({
  type: TEMPLATE_DELETE_COLUMN,
  undoable: true,
  payload: { fieldId, columnId }
});

export const templateEditorUpdateDataLayoutPositionProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_DATALAYOUTPOSITION_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateDataLayoutSectionProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_DATALAYOUTSECTION_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorUpdateDataLayoutTabProperty = (keyPaths: KeyPath[], value: TemplateEditorValue): TemplateEditorAction => ({
  type: TEMPLATE_UPDATE_DATALAYOUTTAB_PROPERTY,
  undoable: true,
  payload: { keyPaths, value }
});

export const templateEditorCloseError = (): TemplateEditorAction => ({
  type: TEMPLATE_CLOSE_ERROR
});

export const templateEditorCloseErrorMessage = (): TemplateEditorAction => ({
  type: TEMPLATE_CLOSE_ERROR_MESSAGE
});

export const templateEditorRemoveView = (viewType: TemplateViewType): TemplateEditorAction => ({
  type: TEMPLATE_REMOVE_VIEW,
  undoable: true,
  payload: { viewType }
});

export const templateEditorAddView = (viewType: TemplateViewType): TemplateEditorAction => ({
  type: TEMPLATE_ADD_VIEW,
  undoable: true,
  payload: { viewType }
});

export const getTemplateRequest = (): TemplateEditorAction => ({
  type: TEMPLATE_GET_REQUEST,
  payload: {
    isFetching: true,
    error: null
  }
});

export const getTemplateSuccess = (data: Template, addingFormView: boolean, versionId?: string): TemplateEditorAction => ({
  type: TEMPLATE_GET_SUCCESS,
  payload: {
    error: null,
    isFetching: false,
    initialTemplateJSON: JSON.stringify(data),
    template: data,
    templateLoaded: true,
    addingFormView,
    readOnly: !!versionId
  }
});

export const getTemplateFailure = (error: string): TemplateEditorAction => ({
  type: TEMPLATE_GET_FAILURE,
  payload: {
    error: {
      message: error
    },
    isFetching: false
  }
});

export const saveTemplateRequest = (): TemplateEditorAction => ({
  type: TEMPLATE_SAVE_REQUEST,
  payload: {
    isSaving: true,
    isFetching: false
  }
});

export const saveTemplateSuccess = (response: AxiosResponse<{ lastUpdatedDate: string }>): TemplateEditorAction => ({
  type: TEMPLATE_SAVE_SUCCESS,
  payload: {
    isSaving: false,
    isDirty: false,
    lastSaveDate: moment.utc(response.data.lastUpdatedDate).local().format(getDateTimeFormat()),
    error: null
  }
});

export const publishTemplateRequest = (): TemplateEditorAction => ({
  type: TEMPLATE_PUBLISH_REQUEST,
  payload: {
    isPublishing: true,
    isFetching: false,
    error: null
  }
});

export const publishTemplateSuccess = (response: AxiosResponse<{ versionId: string; createdDate: string }>): TemplateEditorAction => ({
  type: TEMPLATE_PUBLISH_SUCCESS,
  payload: {
    isPublishing: false,
    isDirty: false,
    lastSaveDate: moment.utc(response.data.createdDate).local().format(getDateTimeFormat())
  },
  versionId: response.data.versionId
});

export const templateEditorSwitchActiveEditor = (editorType: EditorTypes): TemplateEditorAction => ({
  type: TEMPLATE_SWITCH_ACTIVE_EDITOR,
  payload: {
    editorType: editorType
  }
});

export const templateEdtiorShowFieldsWithElements = (showFieldsWithElements: boolean): TemplateEditorAction => ({
  type: TEMPLATE_EDTIOR_SHOW_FIELDS_WITH_ELEMENTS,
  payload: {
    showFieldsWithElements: showFieldsWithElements
  }
});

export const templateFieldAddBeginDrag = (): TemplateEditorAction => ({
  type: TEMPLATE_ADD_FIELD_BEGIN_DRAG,
  undoable: false
});

export const templateFieldAddEndDrag = (): TemplateEditorAction => ({
  type: TEMPLATE_ADD_FIELD_END_DRAG,
  undoable: false
});
